import React, { useState, useMemo } from 'react';
import { Modal, Input, Checkbox, Button, Empty, Typography, message, Spin } from 'antd';
import { SearchOutlined, PlusOutlined, UserAddOutlined } from '@ant-design/icons';
import { createAccountApi } from '../../api/accounts';

const { Text } = Typography;

export default function LabourPickerDialog({ open, onClose, onConfirm, labourAccounts, excludeIds = [], onLabourCreated }) {
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState([]);
  const [showAdd, setShowAdd]     = useState(false);
  const [newName, setNewName]     = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [creating, setCreating]   = useState(false);

  const available = useMemo(() =>
    labourAccounts.filter(
      (a) => !excludeIds.includes(a.id) &&
        (search === '' || a.name.toLowerCase().includes(search.toLowerCase()))
    ),
    [labourAccounts, excludeIds, search]
  );

  const toggle = (id) =>
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleConfirm = () => {
    const picked = labourAccounts.filter((a) => selected.includes(a.id));
    onConfirm(picked);
    reset();
  };

  const reset = () => { setSelected([]); setSearch(''); setShowAdd(false); setNewName(''); setNewMobile(''); };

  const handleClose = () => { reset(); onClose(); };

  const handleCreateLabour = async () => {
    if (!newName.trim()) { message.warning('Enter a name'); return; }
    setCreating(true);
    try {
      const res = await createAccountApi({ type: 'labour', name: newName.trim(), mobile: newMobile.trim() });
      message.success('Labour added');
      onLabourCreated?.(res.data);
      setShowAdd(false);
      setNewName('');
      setNewMobile('');
    } catch {
      message.error('Failed to create labour');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      title="Select Labour"
      open={open}
      onCancel={handleClose}
      width={420}
      zIndex={1100}
      footer={
        showAdd ? null : [
          <Button key="cancel" onClick={handleClose}>Cancel</Button>,
          <Button key="ok" type="primary" disabled={selected.length === 0} onClick={handleConfirm}
            style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}>
            Add {selected.length > 0 ? `(${selected.length})` : ''}
          </Button>,
        ]
      }
    >
      {/* Add New Labour inline form */}
      {showAdd ? (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fafafa' }}>
          <Text strong style={{ display: 'block', marginBottom: 10 }}>New Labour Account</Text>
          <Input
            placeholder="Name *"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ marginBottom: 8 }}
          />
          <Input
            placeholder="Mobile (optional)"
            value={newMobile}
            onChange={(e) => setNewMobile(e.target.value)}
            style={{ marginBottom: 12 }}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button size="small" onClick={() => { setShowAdd(false); setNewName(''); setNewMobile(''); }}>
              Cancel
            </Button>
            <Button
              size="small" type="primary" loading={creating} onClick={handleCreateLabour}
              style={{ background: '#1a6b2a', borderColor: '#1a6b2a' }}
            >
              Save Labour
            </Button>
          </div>
        </div>
      ) : (
        <Button
          type="dashed" icon={<UserAddOutlined />}
          style={{ width: '100%', marginBottom: 12, borderColor: '#1a6b2a', color: '#1a6b2a' }}
          onClick={() => setShowAdd(true)}
        >
          Add New Labour
        </Button>
      )}

      <Input
        prefix={<SearchOutlined />}
        placeholder="Search labour..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      {available.length === 0 ? (
        <Empty description="No labour accounts found" imageStyle={{ height: 40 }} />
      ) : (
        <div style={{ maxHeight: 280, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {available.map((a) => (
            <div
              key={a.id}
              onClick={() => toggle(a.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                background: selected.includes(a.id) ? '#f0f9f0' : '#fafafa',
                border: `1px solid ${selected.includes(a.id) ? '#1a6b2a' : '#f0f0f0'}`,
              }}
            >
              <Checkbox checked={selected.includes(a.id)} onChange={() => toggle(a.id)} />
              <div>
                <div style={{ fontWeight: 500, fontSize: 14 }}>{a.name}</div>
                {a.mobile && <Text type="secondary" style={{ fontSize: 12 }}>{a.mobile}</Text>}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
